import { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import Accordion from 'react-bootstrap/Accordion';
import { i18n } from 'app/utils/i18n';
import { createNewDraft } from 'app/utils/draft';
import { useDelayedSave } from 'app/utils/hooks/delayed-save';
import { draftQueryKey, NO_DRAFT } from 'app/api/draft-queries';
import { registerPendingDesignDraftSave } from './utils/pending-design-saves';
import { variableSchema, VariableGroup } from './variable-schema';
import { useDesignDraft } from './hooks/useDesignDraft';
import { useResetOnDraftDiscard } from './hooks/useResetOnDraftDiscard';
import { commitValue } from './utils/commit-value';
import { computeDerivedColors } from './utils/derived-colors';
import { VariableGroupPanel } from './components/VariableGroupPanel';
import { PageDiagram, ActiveZone } from './components/PageDiagram';
import { PreviewModal } from './components/PreviewModal';
import { HeaderFooterPreview } from './components/HeaderFooterPreview';
import { TocPreview } from './components/TocPreview';
import { FormClassStylesInline } from './FormClassStylesInline';
import { useBuiltinCssSlots } from './BuiltinCssSlots';
import { HoveredTarget } from './utils/builtin-selectors';
import './DesignConfigurator.scss';

const typographyGroups: VariableGroup[] = [
  'typography-h1',
  'typography-h2',
  'typography-h3',
  'typography-h4',
  'typography-h5',
  'typography-h6',
  'typography-body',
  'typography-td',
  'typography-th',
  'typography-li',
  'typography-scaling',
];

const layoutGroups: VariableGroup[] = ['layout-padding', 'layout-margin', 'layout-spacing', 'block-spacing'];

const INDEPENDENT_TYPOGRAPHY_KEYS = [
  'headerFontSize',
  'headerLineHeight',
  'headerLetterSpacing',
  'footerFontSize',
  'footerLineHeight',
  'footerLetterSpacing',
] as const;

const derivedColorGroups: VariableGroup[] = ['derived-primary', 'derived-secondary', 'derived-tertiary', 'derived-quaternary'];

function accordionToZone(key: string | null): ActiveZone {
  switch (key) {
    case 'paper':
      return 'paper';
    case '0':
      return 'typography';
    case '1':
      return 'layout';
    case '2':
      return 'header-footer';
    case '3':
      return 'table-of-contents';
    default:
      return null;
  }
}

export function DesignConfigurator() {
  const queryClient = useQueryClient();
  const { documentId, pDocument, style, draft, draftEntry, saveDraft } = useDesignDraft({ styleType: 'graphical-variables' });
  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);
  const [hoveredGroup, setHoveredGroup] = useState<VariableGroup | null>(null);
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [hoveredInner, setHoveredInner] = useState<HoveredTarget | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const handleInnerHover = useCallback((hovered: HoveredTarget | null) => setHoveredInner(hovered), []);
  const { renderSlot: renderBuiltinSlot } = useBuiltinCssSlots({ onInnerHover: handleInnerHover });

  const activeZone: ActiveZone = hoveredGroup ?? accordionToZone(activeAccordion);
  const handleGroupHover = useCallback((group: VariableGroup | null) => setHoveredGroup(group), []);
  const handleFieldHover = useCallback((key: string | null) => setHoveredField(key), []);

  const documentVariables: Record<string, string> = style.data?.content ?? {};
  const draftVariables: Record<string, string> = draftEntry?.style ?? {};
  const [stateVariables, setStateVariables] = useState<Record<string, string> | null>(null);

  useResetOnDraftDiscard(
    draft.data,
    useCallback(() => setStateVariables(null), [])
  );

  const variables: Record<string, string> = { ...documentVariables, ...draftVariables, ...(stateVariables ?? {}) };
  const derivedValues = computeDerivedColors(variables);
  const allVariables: Record<string, string> = { ...variables, ...derivedValues };

  const [pushDraft, cancelPushDraft] = useDelayedSave((newVariables: Record<string, string>) => {
    const entry = {
      type: 'graphical-variables' as const,
      style: newVariables,
    };
    const latestDraft = queryClient.getQueryData(draftQueryKey(documentId)) ?? NO_DRAFT;
    const newDraft = createNewDraft(pDocument, latestDraft, entry);
    saveDraft(newDraft);
  }, 1000);
  useEffect(() => registerPendingDesignDraftSave(cancelPushDraft), [cancelPushDraft]);

  const pageSizeDimensions: Record<string, { width: string; height: string }> = {
    'A4 portrait': { width: '794px', height: '1123px' },
    'A4 landscape': { width: '1123px', height: '794px' },
    'A3 portrait': { width: '1123px', height: '1587px' },
    'A3 landscape': { width: '1587px', height: '1123px' },
    'A5 portrait': { width: '559px', height: '794px' },
    'A5 landscape': { width: '794px', height: '559px' },
    'letter portrait': { width: '816px', height: '1056px' },
    'letter landscape': { width: '1056px', height: '816px' },
    'legal portrait': { width: '816px', height: '1344px' },
    'legal landscape': { width: '1344px', height: '816px' },
  };

  const handleChange = (key: string, rawValue: string) => {
    const def = variableSchema.find((v) => v.key === key);
    if (!def) return;

    const commit = commitValue(def, rawValue);
    if (!commit) return;

    const newVariables: Record<string, string | null> = { ...variables, [key]: commit.value };

    if (key === 'pageSize' && pageSizeDimensions[rawValue]) {
      const dims = pageSizeDimensions[rawValue];
      newVariables.pageWidth = dims.width;
      newVariables.pageHeight = dims.height;
    }

    setStateVariables(newVariables as Record<string, string>);
    pushDraft(newVariables as Record<string, string>);
  };

  const isIndependentTypography = INDEPENDENT_TYPOGRAPHY_KEYS.some((k) => {
    const v = variables[k];
    return v !== undefined && v !== null && v !== '';
  });

  const handleToggleIndependentTypography = () => {
    const next: Record<string, string | null> = { ...variables };
    if (isIndependentTypography) {
      for (const k of INDEPENDENT_TYPOGRAPHY_KEYS) {
        next[k] = null;
      }
    } else {
      const fs = variables.headerFooterFontSize ?? '9px';
      const lh = variables.headerFooterLineHeight ?? '12px';
      const ls = variables.headerFooterLetterSpacing ?? '0.08em';
      next.headerFontSize = fs;
      next.headerLineHeight = lh;
      next.headerLetterSpacing = ls;
      next.footerFontSize = fs;
      next.footerLineHeight = lh;
      next.footerLetterSpacing = ls;
    }
    setStateVariables(next as Record<string, string>);
    pushDraft(next as Record<string, string>);
  };

  return (
    <div className="design-configurator">
      <h2 className="mb-3">{i18n('dc.title')}</h2>

      <div className="d-flex flex-column align-items-center mb-3">
        <PageDiagram activeZone={activeZone} hoveredField={hoveredField} variables={variables} />
        <button className="btn btn-sm btn-primary mt-2" onClick={() => setShowPreview(true)}>
          <i className="fa-regular fa-eye me-1" />
          {i18n('dc.preview')}
        </button>
      </div>

      <PreviewModal show={showPreview} onClose={() => setShowPreview(false)} documentId={documentId} />

      <Accordion onSelect={(key) => setActiveAccordion(key as string | null)}>
        <Accordion.Item eventKey="paper">
          <Accordion.Header>{i18n('dc.accordion.paper')}</Accordion.Header>
          <Accordion.Body>
            <VariableGroupPanel group="paper" variables={variables} onChange={handleChange} onHover={handleGroupHover} onFieldHover={handleFieldHover} />
            <VariableGroupPanel
              group="paper-background"
              variables={variables}
              onChange={handleChange}
              onHover={handleGroupHover}
              onFieldHover={handleFieldHover}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="0">
          <Accordion.Header>{i18n('dc.accordion.typography')}</Accordion.Header>
          <Accordion.Body>
            {typographyGroups.map((group) => (
              <VariableGroupPanel
                key={group}
                group={group}
                variables={variables}
                onChange={handleChange}
                onHover={handleGroupHover}
                onFieldHover={handleFieldHover}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>{i18n('dc.accordion.layout')}</Accordion.Header>
          <Accordion.Body>
            {layoutGroups.map((group) => (
              <VariableGroupPanel
                key={group}
                group={group}
                variables={variables}
                onChange={handleChange}
                onHover={handleGroupHover}
                onFieldHover={handleFieldHover}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="2">
          <Accordion.Header>{i18n('dc.accordion.header-footer')}</Accordion.Header>
          <Accordion.Body>
            <HeaderFooterPreview hoveredInner={hoveredInner} />
            <VariableGroupPanel group="header" variables={variables} onChange={handleChange} onHover={handleGroupHover} onFieldHover={handleFieldHover} />
            {renderBuiltinSlot('area-header')}
            <VariableGroupPanel group="footer" variables={variables} onChange={handleChange} onHover={handleGroupHover} onFieldHover={handleFieldHover} />
            {renderBuiltinSlot('area-footer')}
            <div className="d-flex justify-content-start mb-2 mt-3">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="independent-typography-toggle"
                  checked={isIndependentTypography}
                  onChange={handleToggleIndependentTypography}
                />
                <label className="form-check-label fs-7" htmlFor="independent-typography-toggle">
                  {i18n('dc.var.independent-typography')}
                </label>
              </div>
            </div>
            {isIndependentTypography ? (
              <>
                <VariableGroupPanel
                  group="header-typography"
                  variables={variables}
                  onChange={handleChange}
                  onHover={handleGroupHover}
                  onFieldHover={handleFieldHover}
                />
                <VariableGroupPanel
                  group="footer-typography"
                  variables={variables}
                  onChange={handleChange}
                  onHover={handleGroupHover}
                  onFieldHover={handleFieldHover}
                />
              </>
            ) : (
              <VariableGroupPanel
                group="header-footer-text"
                variables={variables}
                onChange={handleChange}
                onHover={handleGroupHover}
                onFieldHover={handleFieldHover}
              />
            )}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="3">
          <Accordion.Header>{i18n('dc.accordion.table-of-contents')}</Accordion.Header>
          <Accordion.Body>
            <TocPreview hoveredInner={hoveredInner} />
            <VariableGroupPanel
              group="table-of-contents"
              variables={variables}
              onChange={handleChange}
              onHover={handleGroupHover}
              onFieldHover={handleFieldHover}
            />
            {renderBuiltinSlot('editor-table-of-content--container')}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="4">
          <Accordion.Header>{i18n('dc.accordion.custom-styles')}</Accordion.Header>
          <Accordion.Body>
            <FormClassStylesInline />
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <hr className="my-4 border-gray-300" />
      <h2 className="mb-3">{i18n('dc.derived-readonly')}</h2>

      <Accordion>
        <Accordion.Item eventKey="derived-colors">
          <Accordion.Header>{i18n('dc.accordion.derived-colors')}</Accordion.Header>
          <Accordion.Body>
            {derivedColorGroups.map((group) => (
              <VariableGroupPanel key={group} group={group} variables={allVariables} onChange={handleChange} />
            ))}
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="derived-neutrals">
          <Accordion.Header>{i18n('dc.accordion.neutral-colors')}</Accordion.Header>
          <Accordion.Body>
            <VariableGroupPanel group="derived-white" variables={allVariables} onChange={handleChange} />
            <VariableGroupPanel group="derived-black" variables={allVariables} onChange={handleChange} />
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
}
